import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { D, designError, designErrorList, fmtDate, label, useDesignMe } from "@/lib/designApi";
import {
  DataTable, ErrorList, Field, HistoryPanel, ItemPicker, NoAccess, PageHeader, Section, SimpleSelect, StatusBadge,
  WorkflowBar, useQueryId,
} from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const newLine = () => ({ component_no: "", qty: 1, uom: "", scrap_pct: 0, alternates: [], find_no: "", remarks: "" });

function LinesEditor({ itemNo, lines, setLines, disabled }) {
  const upd = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="bom-lines-editor">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-100">
            <th className="py-2 pr-2 w-14">Find</th><th className="py-2 pr-2 w-48">Component</th><th className="py-2 pr-2">Name</th>
            <th className="py-2 pr-2 w-24">Qty</th><th className="py-2 pr-2 w-20">UOM</th><th className="py-2 pr-2 w-24">Scrap %</th>
            <th className="py-2 pr-2 w-56">Alternates (comma sep.)</th><th className="py-2 pr-2">Remarks</th><th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-slate-50 align-top">
              <td className="py-1 pr-2"><Input value={l.find_no} disabled={disabled} onChange={(e) => upd(i, "find_no", e.target.value)} /></td>
              <td className="py-1 pr-2">{disabled ? <span className="mono">{l.component_no}</span> :
                <ItemPicker value={l.component_no} excludeNumber={itemNo} testid={`bom-comp-${i}`}
                  onChange={(v, it) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, component_no: v, component_name: it?.name ?? x.component_name, uom: x.uom || it?.uom || "" } : x)))} />}</td>
              <td className="py-1 pr-2 text-slate-600 text-xs pt-2.5">{l.component_name}</td>
              <td className="py-1 pr-2"><Input type="number" min="0" step="any" value={l.qty} disabled={disabled} onChange={(e) => upd(i, "qty", e.target.value)} /></td>
              <td className="py-1 pr-2"><Input value={l.uom} disabled={disabled} onChange={(e) => upd(i, "uom", e.target.value)} /></td>
              <td className="py-1 pr-2"><Input type="number" min="0" max="99" step="any" value={l.scrap_pct} disabled={disabled} onChange={(e) => upd(i, "scrap_pct", e.target.value)} /></td>
              <td className="py-1 pr-2"><Input value={(l.alternates || []).map((a) => a.component_no).join(", ")} disabled={disabled}
                onChange={(e) => upd(i, "alternates", e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean).map((n, p) => ({ component_no: n, priority: p + 1, remarks: "" })))} /></td>
              <td className="py-1 pr-2"><Input value={l.remarks} disabled={disabled} onChange={(e) => upd(i, "remarks", e.target.value)} /></td>
              <td className="py-1">{!disabled && <button className="text-slate-400 hover:text-red-600 mt-2" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!disabled && <Button size="sm" variant="outline" className="mt-2" onClick={() => setLines((ls) => [...ls, newLine()])} data-testid="bom-add-line"><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>}
    </div>
  );
}

const clean = (lines) => lines.filter((l) => l.component_no).map((l) => ({
  component_no: l.component_no.trim().toUpperCase(), qty: Number(l.qty), uom: l.uom || "", scrap_pct: Number(l.scrap_pct) || 0,
  alternates: l.alternates || [], find_no: l.find_no || "", remarks: l.remarks || "",
}));

function Explosion({ id }) {
  const [qty, setQty] = useState(1);
  const [data, setData] = useState(null);
  const run = useCallback(() => api.get(`${D}/boms/${id}/explode`, { params: { qty } }).then((r) => setData(r.data)).catch((e) => toast.error(designError(e))), [id, qty]);
  useEffect(() => { run(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end">
        <Field label="Build quantity"><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-32" /></Field>
        <Button variant="outline" onClick={run}>Explode</Button>
      </div>
      {data && (
        <>
          <Section title="Indented multi-level BOM">
            <table className="w-full text-sm" data-testid="bom-explosion">
              <thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b"><th className="py-1.5">Lvl</th><th>Part</th><th>Name</th><th>Qty/parent</th><th>Scrap %</th><th>Extended</th><th>UOM</th><th>Status</th></tr></thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 mono text-slate-500">{r.level}</td>
                    <td className="mono" style={{ paddingLeft: `${(r.level - 1) * 16}px` }}>{r.has_children ? "▸ " : ""}{r.component_no}</td>
                    <td className="text-slate-700">{r.component_name}</td>
                    <td className="mono">{r.qty}</td><td className="mono">{r.scrap_pct || ""}</td>
                    <td className="mono font-semibold">{r.extended_qty}</td><td>{r.uom}</td>
                    <td>{r.item_status && <StatusBadge status={r.item_status} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
          <Section title="Summarised leaf requirement">
            <table className="w-full text-sm"><tbody>
              {data.summary.map((s) => <tr key={s.component_no} className="border-b border-slate-50"><td className="py-1 mono">{s.component_no}</td><td>{s.component_name}</td><td className="mono font-semibold text-right">{s.total_qty}</td></tr>)}
            </tbody></table>
          </Section>
        </>
      )}
    </div>
  );
}

function Cost({ id }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`${D}/boms/${id}/cost`).then((r) => setData(r.data)).catch((e) => setErr(designError(e))); }, [id]);
  if (err) return <div className="text-sm text-slate-500">{err}</div>;
  if (!data) return null;
  const r = data.root;
  return (
    <div className="space-y-3" data-testid="bom-cost">
      <div className="grid grid-cols-3 gap-3">
        {[["Material", r.material_cost], ["Conversion (routing)", r.conversion_cost], ["Total standard cost", r.total_cost]].map(([t, v]) => (
          <div key={t} className="bg-white border border-slate-200 rounded-lg p-3"><div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{t}</div><div className="text-xl font-extrabold mono">₹ {Number(v).toFixed(2)}</div></div>
        ))}
      </div>
      {data.missing_costs.length > 0 && <ErrorList errors={[`No cost for: ${data.missing_costs.join(", ")} — set Std cost on the item or the ERP item master.`]} />}
      <Section title="Cost by component (one level)">
        <table className="w-full text-sm"><thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b"><th className="py-1.5">Part</th><th>Name</th><th>Qty incl. scrap</th><th>Unit cost</th><th>Extended</th><th>Source</th></tr></thead><tbody>
          {r.children.map((c) => (
            <tr key={c.component_no} className="border-b border-slate-50"><td className="py-1 mono">{c.component_no}</td><td>{c.component_name}</td><td className="mono">{Number(c.qty).toFixed(3)}</td>
              <td className="mono">{c.unit_cost.toFixed(2)}</td><td className="mono font-semibold">{c.extended_cost.toFixed(2)}</td>
              <td className="text-xs text-slate-500">{c.leaf ? label(data.cost_sources[c.component_no]) : "rolled-up"}</td></tr>
          ))}
        </tbody></table>
      </Section>
    </div>
  );
}

function Compare({ rec }) {
  const [other, setOther] = useState("");
  const [data, setData] = useState(null);
  const revs = (rec.revisions || []).filter((r) => r.id !== rec.id);
  useEffect(() => {
    if (!other) return;
    api.get(`${D}/boms/compare`, { params: { a: other, b: rec.id } }).then((r) => setData(r.data)).catch((e) => toast.error(designError(e)));
  }, [other, rec.id]);
  if (!revs.length) return <div className="text-sm text-slate-500">Only one revision exists — nothing to compare yet.</div>;
  return (
    <div className="space-y-3">
      <Field label={`Compare Rev ${rec.revision} with`}><div className="w-48 max-md:w-full"><SimpleSelect value={other} onChange={setOther} options={revs.map((r) => ({ value: r.id, label: `Rev ${r.revision} (${label(r.status)})` }))} /></div></Field>
      {data && (
        <div className="space-y-2 text-sm" data-testid="bom-compare">
          <div className="text-xs text-slate-500">{data.summary.added} added · {data.summary.removed} removed · {data.summary.changed} changed · {data.unchanged_count} unchanged</div>
          {data.added.map((l) => <div key={`a${l.component_no}`} className="bg-emerald-50 text-emerald-800 rounded px-2 py-1">+ <span className="mono">{l.component_no}</span> {l.component_name} × {l.qty}</div>)}
          {data.removed.map((l) => <div key={`r${l.component_no}`} className="bg-red-50 text-red-700 rounded px-2 py-1 line-through">− <span className="mono">{l.component_no}</span> {l.component_name} × {l.qty}</div>)}
          {data.changed.map((c) => (
            <div key={`c${c.component_no}`} className="bg-amber-50 text-amber-800 rounded px-2 py-1">~ <span className="mono">{c.component_no}</span>{" "}
              {Object.entries(c.changes).map(([k, v]) => `${label(k)}: ${Array.isArray(v.from) ? v.from.join(",") || "—" : v.from} → ${Array.isArray(v.to) ? v.to.join(",") || "—" : v.to}`).join(" · ")}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function BomDetail({ id, onChanged, onOpen }) {
  const { can } = useDesignMe();
  const [rec, setRec] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => {
    api.get(`${D}/boms/${id}`).then((r) => { setRec(r.data); setLines(r.data.lines || []); setNotes(r.data.notes || ""); }).catch((e) => toast.error(designError(e)));
  }, [id]);
  useEffect(load, [load]);
  if (!rec) return null;
  const editable = rec.status === "draft" && can("create_edit");
  const validate = async () => {
    const { data } = await api.post(`${D}/boms/validate`, { item_no: rec.item_no, lines: clean(lines) });
    setErrors(data.errors);
    if (data.valid) toast.success("BOM is valid");
  };
  const save = async () => {
    setErrors([]);
    try {
      await api.patch(`${D}/boms/${id}`, { lines: clean(lines), notes });
      toast.success("Saved");
      load();
      onChanged?.();
    } catch (e) {
      const l = designErrorList(e);
      setErrors(l.length ? l : [designError(e)]);
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{rec.number} · Rev {rec.revision}</div>
        <div className="text-xl font-bold text-slate-900">{rec.item_no} — {rec.item_name}</div>
      </div>
      <WorkflowBar entity="boms" record={rec} onChanged={(n) => (n?.id && n.id !== rec.id ? onOpen(n.id) : (load(), onChanged?.()))} />
      <Tabs defaultValue="lines">
        <TabsList>
          <TabsTrigger value="lines">Lines ({rec.lines?.length || 0})</TabsTrigger>
          <TabsTrigger value="explode">Multi-level</TabsTrigger>
          {can("costing_view") && <TabsTrigger value="cost">Cost roll-up</TabsTrigger>}
          <TabsTrigger value="compare">Compare revisions</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="lines" className="space-y-3">
          <LinesEditor itemNo={rec.item_no} lines={lines} setLines={setLines} disabled={!editable} />
          <Field label="Notes"><Textarea rows={2} value={notes} disabled={!editable} onChange={(e) => setNotes(e.target.value)} /></Field>
          <ErrorList errors={errors} />
          {editable && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={validate}><ShieldCheck className="w-4 h-4 mr-1" />Validate</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="bom-save">Save draft</Button>
            </div>
          )}
        </TabsContent>
        <TabsContent value="explode"><Explosion id={rec.id} /></TabsContent>
        <TabsContent value="cost"><Cost id={rec.id} /></TabsContent>
        <TabsContent value="compare"><Compare rec={rec} /></TabsContent>
        <TabsContent value="history"><HistoryPanel entity="boms" record={rec} onOpenRevision={onOpen} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function DesignBoms() {
  const { me, can, error: meErr } = useDesignMe();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(useQueryId());
  const [creating, setCreating] = useState(false);
  const [itemNo, setItemNo] = useState("");
  const [lines, setLines] = useState([newLine()]);
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => api.get(`${D}/boms`, { params: { q } }).then((r) => setRows(r.data)).catch(() => setRows([])), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("new")) { setCreating(true); setItemNo(p.get("item") || ""); }
  }, []);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const create = async () => {
    setErrors([]);
    try {
      const { data } = await api.post(`${D}/boms`, { item_no: itemNo.trim().toUpperCase(), lines: clean(lines) });
      toast.success(`Created ${data.number}`);
      setCreating(false);
      setLines([newLine()]);
      setItemNo("");
      load();
      setOpenId(data.id);
    } catch (e) {
      const l = designErrorList(e);
      setErrors(l.length ? l : [designError(e)]);
    }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-boms-page">
      <PageHeader title="Bills of Material" subtitle="Multi-level BOMs with scrap %, alternates, cycle / self-reference checks, revision compare and cost roll-up">
        {can("create_edit") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="design-new-bom"><Plus className="w-4 h-4 mr-1.5" />New BOM</Button>}
      </PageHeader>
      <div className="relative w-72 max-md:w-full">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input className="pl-9" placeholder="Parent part no / name…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <DataTable rows={rows} onRowClick={(r) => setOpenId(r.id)} testid="design-boms-table"
        columns={[
          { key: "number", title: "BOM", cellClass: "mono font-semibold" },
          { key: "revision", title: "Rev", cellClass: "mono" },
          { key: "item_no", title: "Parent", cellClass: "mono" },
          { key: "item_name", title: "Parent name", cellClass: "max-w-xs truncate" },
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} stage={r.review_stage} /> },
          { key: "ecn_number", title: "ECN" },
          { key: "updated_at", title: "Updated", render: (r) => fmtDate(r.updated_at) },
        ]} />
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New BOM</DialogTitle></DialogHeader>
          <Field label="Parent part number *"><div className="w-72 max-md:w-full"><ItemPicker value={itemNo} onChange={setItemNo} testid="bom-parent" /></div></Field>
          <LinesEditor itemNo={itemNo} lines={lines} setLines={setLines} />
          <ErrorList errors={errors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!itemNo} onClick={create} data-testid="bom-create">Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto">
          <SheetHeader><SheetTitle>Bill of Material</SheetTitle></SheetHeader>
          {openId && <BomDetail key={openId} id={openId} onChanged={load} onOpen={setOpenId} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
